import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Badge,
  Spinner,
} from "reactstrap";
import classnames from "classnames";
import toast from "react-hot-toast";
import MediaTab from "./MediaTab";
import DeviceTab from "./DeviceTab";
import AddDeviceModal from "./AddDeviceModal";
import CampaignAssignService from "../../../services/OrderingServices/CampaignAssignService";

const CampaignTabsCard = ({
  campaign,
  activeTab,
  setActiveTab,

  // Media props
  selectedAds,
  setSelectedAds,
  handleUnassign,
  unassigning,
  handleOpenModal,
  modalLoading,
  toggleAdSelection,
  getMediaUrl,
  onDurationUpdate,
  onRefreshCampaign,

  // Device props
  devices,
  devicesLoading,
  filteredDevices,
  selectedDevices,
  setSelectedDevices,
  deviceSearch,
  setDeviceSearch,
  toggleDeviceSelection,
  handleAssignDevices,
  handleUnassignDevices,
  assigningDevices,
  unassigningDevices,
  getDeviceImageUrl,

  // Device Modal props
  deviceModalOpen,
  setDeviceModalOpen,
  availableDevices,
  selectedNewDevices,
  devicesLoadingApi,
  handleOpenDeviceModal,
  toggleNewDeviceSelection,
  handleAssignNewDevices,
  fetchDevicesBySearch,
  setAvailableDevices,
  isSearching,
  assignedDevices = [],
}) => {
  // Reorder mode state
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderedAds, setReorderedAds] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // Initialize reordered ads when entering reorder mode
  useEffect(() => {
    if (isReorderMode && campaign.ads) {
      setReorderedAds([...campaign.ads]);
    }
  }, [isReorderMode, campaign.ads]);

  // Drag handlers for reordering
  const handleReorderDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = "move";
    setDraggedIndex(index);
  };

  const handleReorderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleReorderDrop = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updatedAds = [...reorderedAds];
    const [movedItem] = updatedAds.splice(draggedIndex, 1);
    updatedAds.splice(index, 0, movedItem);

    setReorderedAds(updatedAds);
    setDraggedIndex(null);
  };

  const handleReorderDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleEnterReorderMode = () => {
    setIsReorderMode(true);
  };

  const handleCancelReorder = () => {
    setIsReorderMode(false);
    setReorderedAds([]);
    setDraggedIndex(null);
  };

  const handleSaveReorder = async () => {
    try {
      setIsSavingOrder(true);
      
      // Prepare ad IDs in the new order
      const adIds = reorderedAds.map((ad) => ad.id);

      // Call the API to save the new order
      await CampaignAssignService.reorderCampaignAds(campaign.id, adIds);

      // Exit reorder mode first
      setIsReorderMode(false);
      setReorderedAds([]);
      
      // Show success message
      toast.success(`✓ Media order updated successfully!`);
      
      // Refresh the entire campaign data to show updated order
      if (onRefreshCampaign) {
        await onRefreshCampaign();
      }
      
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error(
        error.response?.data?.message || "Failed to update media order"
      );
    } finally {
      setIsSavingOrder(false);
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="bg-light border-bottom d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <Nav tabs className="border-0">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "media" })}
                onClick={() => setActiveTab("media")}
                style={{ cursor: "pointer" }}
              >
                Media ({campaign.ads?.length || 0})
              </NavLink>
            </NavItem>

            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "devices" })}
                onClick={() => setActiveTab("devices")}
                style={{ cursor: "pointer" }}
              >
                Devices ({devices?.length || 0})
              </NavLink>
            </NavItem>
          </Nav>
          
          {activeTab === "media" && isReorderMode && (
            <Badge color="info" className="me-2">
              <i className="ri-drag-move-2-line me-1"></i>
              Reorder Mode - Drag to reorder
            </Badge>
          )}
        </div>

        {/* Buttons only when Media tab is active */}
        {activeTab === "media" && (
          <div className="d-flex gap-2">
            {!isReorderMode ? (
              <>
                {campaign.ads?.length > 0 && (
                  <>
                    <Button
                      size="sm"
                      outline
                      onClick={() =>
                        selectedAds.length === campaign.ads.length
                          ? setSelectedAds([])
                          : setSelectedAds([...campaign.ads])
                      }
                    >
                      {selectedAds.length === campaign.ads.length
                        ? "Deselect All"
                        : "Select All"}
                    </Button>

                    <Button
                      color="danger"
                      size="sm"
                      onClick={handleUnassign}
                      disabled={selectedAds.length === 0 || unassigning}
                    >
                      Unassign ({selectedAds.length})
                    </Button>

                    <Button
                      color="primary"
                      outline
                      size="sm"
                      onClick={handleEnterReorderMode}
                    >
                      <i className="ri-drag-move-line me-1"></i>
                      Reorder
                    </Button>
                  </>
                )}

                <Button
                  color="success"
                  size="sm"
                  onClick={handleOpenModal}
                  disabled={modalLoading}
                >
                  Add New
                </Button>
              </>
            ) : (
              <>
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={handleCancelReorder}
                  disabled={isSavingOrder}
                >
                  Cancel
                </Button>
                <Button
                  color="success"
                  size="sm"
                  onClick={handleSaveReorder}
                  disabled={isSavingOrder}
                >
                  {isSavingOrder ? (
                    <>
                      <i className="ri-loader-4-line me-1"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-1"></i>
                      Save Order
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        )}

        {activeTab === "devices" && (
          <div className="d-flex gap-2">
            {devices?.length > 0 && (
              <>
                <Button
                  size="sm"
                  outline
                  onClick={() =>
                    selectedDevices.length === devices.length
                      ? setSelectedDevices([])
                      : setSelectedDevices([...devices])
                  }
                >
                  {selectedDevices.length === devices.length
                    ? "Deselect All"
                    : "Select All"}
                </Button>

                <Button
                  color="danger"
                  size="sm"
                  onClick={handleUnassignDevices}
                  disabled={selectedDevices.length === 0 || unassigningDevices}
                >
                  Unassign ({selectedDevices.length})
                </Button>
              </>
            )}

            <Button
              color="success"
              size="sm"
              onClick={handleOpenDeviceModal}
              disabled={devicesLoadingApi}
            >
              {devicesLoadingApi ? (
                <>
                  <Spinner size="sm" className="me-2" /> Loading...
                </>
              ) : (
                "Add New"
              )}
            </Button>
          </div>
        )}
      </CardHeader>

      <CardBody>
        {activeTab === "media" ? (
          <MediaTab
            campaign={campaign}
            selectedAds={selectedAds}
            setSelectedAds={setSelectedAds}
            handleUnassign={handleUnassign}
            unassigning={unassigning}
            handleOpenModal={handleOpenModal}
            modalLoading={modalLoading}
            toggleAdSelection={toggleAdSelection}
            getMediaUrl={getMediaUrl}
            onDurationUpdate={onDurationUpdate}
            isReorderMode={isReorderMode}
            reorderedAds={reorderedAds}
            draggedIndex={draggedIndex}
            handleReorderDragStart={handleReorderDragStart}
            handleReorderDragOver={handleReorderDragOver}
            handleReorderDrop={handleReorderDrop}
            handleReorderDragEnd={handleReorderDragEnd}
          />
        ) : (
          <DeviceTab
            campaign={campaign}
            devicesLoading={devicesLoading}
            filteredDevices={filteredDevices}
            selectedDevices={selectedDevices}
            deviceSearch={deviceSearch}
            setDeviceSearch={setDeviceSearch}
            toggleDeviceSelection={toggleDeviceSelection}
            handleAssignDevices={handleAssignDevices}
            handleUnassignDevices={handleUnassignDevices}
            assigningDevices={assigningDevices}
            unassigningDevices={unassigningDevices}
            getDeviceImageUrl={getDeviceImageUrl}
          />
        )}
      </CardBody>

      {/* Add Device Modal */}
      <AddDeviceModal
        deviceModalOpen={deviceModalOpen}
        setDeviceModalOpen={setDeviceModalOpen}
        devicesLoadingApi={devicesLoadingApi}
        availableDevices={availableDevices}
        selectedNewDevices={selectedNewDevices}
        toggleNewDeviceSelection={toggleNewDeviceSelection}
        handleAssignNewDevices={handleAssignNewDevices}
        assigningDevices={assigningDevices}
        getDeviceImageUrl={getDeviceImageUrl}
        fetchDevicesBySearch={fetchDevicesBySearch}
        setAvailableDevices={setAvailableDevices}
        isSearching={isSearching}
        assignedDevices={assignedDevices}
      />
    </Card>
  );
};

export default CampaignTabsCard;
